"use client";

import { useEffect, useState } from "react";

type IntegrityState = {
  level: "none" | "reminder" | "warning" | "review";
  changeCount30d: number;
  riskPoints30d: number;
  reviewRequired: boolean;
  restrictionStatus: "active" | "restricted" | "banned";
  notice: null | {
    id: string;
    level: "reminder" | "warning" | "review";
    message: string;
    status: "active" | "acknowledged";
    acknowledgedAt: string | null;
    reasonCodes: string[];
  };
};

const heading = {
  reminder: "Vui lòng kiểm tra tính chính xác",
  warning: "Thông tin đã thay đổi nhiều lần",
  review: "Hồ sơ cần được xác minh",
};

export function IntegrityNotice() {
  const [state, setState] = useState<IntegrityState | null>(null);
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    fetch("/api/integrity", { cache: "no-store" })
      .then(async (response) => {
        if (!response.ok) throw new Error("INTEGRITY_UNAVAILABLE");
        return response.json() as Promise<IntegrityState>;
      })
      .then(setState)
      .catch(() => setState(null));
  }, []);

  if (!state?.notice || state.level === "none") return null;

  async function acknowledge() {
    if (!state?.notice || busy) return;
    setBusy(true);
    try {
      const response = await fetch("/api/integrity", {
        method: "POST",
        headers: { "content-type": "application/json" },
        body: JSON.stringify({
          action: "acknowledge",
          noticeId: state.notice.id,
        }),
      });
      if (!response.ok) throw new Error("ACKNOWLEDGE_FAILED");
      setState(await response.json() as IntegrityState);
    } finally {
      setBusy(false);
    }
  }

  return (
    <aside
      className={`integrity-banner integrity-${state.notice.level}`}
      aria-live="polite"
      aria-label="Nhắc nhở về tính chính xác của thông tin"
    >
      <span className="integrity-icon" aria-hidden="true">!</span>
      <div>
        <strong>{heading[state.notice.level]}</strong>
        <p>{state.notice.message}</p>
        <small>
          {state.changeCount30d} lần cập nhật trong 30 ngày.
          Hệ thống không tự kết luận gian lận; trường hợp nghiêm trọng phải được
          xem xét trước khi áp dụng hạn chế.
        </small>
      </div>
      <div className="integrity-actions">
        <a href="/onboarding">Kiểm tra hồ sơ</a>
        {state.notice.status === "active" ? (
          <button disabled={busy} onClick={acknowledge} type="button">
            {busy ? "Đang ghi nhận…" : "Tôi đã hiểu"}
          </button>
        ) : (
          <span>Đã ghi nhận</span>
        )}
      </div>
    </aside>
  );
}
